import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "../../../components/Layout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost/securecontrol-backend";

const CreateIncidentType = () => {
  const navigate = useNavigate();
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [userInfo, setUserInfo] = useState(null);

  // États pour les erreurs d'abonnement
  const [abonnementError, setAbonnementError] = useState(false);
  const [abonnementMessage, setAbonnementMessage] = useState("");

  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Fonction pour réinitialiser les erreurs d'abonnement
  const resetAbonnementError = () => {
    setAbonnementError(false);
    setAbonnementMessage("");
  };

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
        
        // Vérifier les permissions (seuls les gestionnaires peuvent créer des types d'incidents)
        if (String(normalizedData.profile).trim() !== "3") {
          setError("Accès refusé. Seuls les gestionnaires peuvent créer des types d'incidents.");
        }
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Validation en temps réel
  useEffect(() => {
    validateField();
  }, [type]);

  const validateField = () => {
    const errors = {};

    if (!type.trim()) {
      errors.type = "Le type d'incident est requis";
    } else if (type.trim().length > 250) {
      errors.type = "Maximum 250 caractères";
    } else if (type.trim().length < 2) {
      errors.type = "Minimum 2 caractères";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation avant soumission
    if (!validateField()) {
      setError("Veuillez corriger les erreurs avant de soumettre");
      return;
    }

    setLoading(true);
    setError("");
    setValidationErrors([]);
    resetAbonnementError();

    try {
      const token = localStorage.getItem("access_token");

      const response = await fetch(
        `${API_BASE_URL}/incidents/type_incident/create_type_incidents.php`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ type_incident: type.trim() }),
        }
      );

      const data = await response.json();

      // 🔍 Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (response.status === 403 && data.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(data.message || "Abonnement inactif");
        setLoading(false);
        return;
      }

      if (data.success) {
        setSuccessMsg("Type d'incident créé avec succès !");
        
        setTimeout(() => {
          navigate("/typeIncidents");
        }, 2000);
      } else {
        if (data.errors && Array.isArray(data.errors)) {
          setValidationErrors(data.errors);
        } else {
          setError(data.message || "Erreur lors de la création");
        }
      }
    } catch (err) {
      setError("Erreur serveur : " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setType("");
    setError("");
    setSuccessMsg("");
    setValidationErrors([]);
    setFieldErrors({});
    resetAbonnementError();
  };

  const handleCancel = () => {
    navigate("/typeIncidents");
  };

  // Afficher un message si l'utilisateur n'est pas gestionnaire
  if (userInfo && String(userInfo.profile).trim() !== "3") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Créer un type d'incident</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/typeIncidents">Types d'incidents</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Créer</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#ffebee',
                color: COLORS.danger
              }}>
                <i className="bi bi-shield-lock" style={{ fontSize: '2rem' }}></i>
              </div>
              <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
              <p className="text-muted">Seuls les gestionnaires peuvent créer des types d'incidents.</p>
              <button
                className="btn mt-3 fw-semibold"
                onClick={() => navigate("/typeIncidents")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Créer un type d'incident</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/typeIncidents">Types d'incidents</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Nouveau Type</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <h5 className="card-title" style={{ 
              color: COLORS.primary,
              borderBottom: `2px solid ${COLORS.lightBg}`,
              paddingBottom: '16px',
              marginBottom: '24px'
            }}>
              <i className="bi bi-clipboard-plus me-2" style={{ color: COLORS.secondary }}></i>
              Nouveau type d'incident
            </h5>

            {/* Affichage des erreurs d'abonnement */}
            {abonnementError && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    {abonnementMessage}
                    <div className="d-flex gap-2 justify-content-center mt-3">
                      <a 
                        href="/abonnements/create"
                        className="btn fw-semibold"
                        style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                        Souscrire à un abonnement
                      </a>
                      <button
                        className="btn fw-semibold"
                        onClick={() => {
                          setAbonnementError(false);
                          setAbonnementMessage("");
                        }}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setAbonnementError(false);
                      setAbonnementMessage("");
                    }}
                  ></button>
                </div>
              </div>
            )}

            {/* Messages */}
            {successMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.success
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                  <div className="flex-grow-1">
                    {successMsg}
                    <div className="mt-2">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Redirection en cours...
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setSuccessMsg("")}
                  ></button>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.danger
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff8e1',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.warning
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">
                    <strong>Veuillez corriger les erreurs suivantes :</strong>
                    <ul className="mb-0 mt-2">
                      {validationErrors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setValidationErrors([])}
                  ></button>
                </div>
              </div>
            )}

            {/* Formulaire */}
            <form onSubmit={handleSubmit}>
              {/* Informations principales */}
              <div className="card mb-4" style={{ 
                borderRadius: '8px',
                border: `1px solid ${COLORS.lightBg}`,
                boxShadow: 'none'
              }}>
                <div className="card-body">
                  <h6 className="card-title mb-4" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-info-circle"></i>
                    </div>
                    Informations principales
                  </h6>
                  
                  {/* CHAMP PRINCIPAL - PLEINE LARGEUR */}
                  <div className="row">
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-clipboard me-1" style={{ color: COLORS.secondary }}></i>
                          Nom du type d'incident <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-pencil-square"></i>
                          </span>
                          <input
                            type="text"
                            className={`form-control ${fieldErrors.type ? "is-invalid" : ""}`}
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            disabled={loading}
                            required
                            maxLength={250}
                            placeholder="Ex : Panne technique, Intrusion, Incendie..."
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                        {fieldErrors.type && (
                          <div className="text-danger mt-2 small d-flex align-items-center">
                            <i className="bi bi-exclamation-circle me-1"></i>
                            {fieldErrors.type}
                          </div>
                        )}
                        <div className="form-text d-flex justify-content-between" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <span>{type.length}/250 caractères</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="d-flex justify-content-between mt-4 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleCancel}
                    disabled={loading}
                    style={{
                      color: COLORS.darkText,
                      borderColor: '#e0e0e0',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleReset}
                    disabled={loading}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                </div>
                
                <button
                  type="submit"
                  className="btn fw-semibold"
                  disabled={loading || Object.keys(fieldErrors).length > 0 || !type.trim()}
                  style={{
                    color: 'white',
                    backgroundColor: loading || Object.keys(fieldErrors).length > 0 || !type.trim() ? '#B0BEC5' : COLORS.primary,
                    borderColor: loading || Object.keys(fieldErrors).length > 0 || !type.trim() ? '#B0BEC5' : COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 32px',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    opacity: loading ? 0.8 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && Object.keys(fieldErrors).length === 0 && type.trim()) {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading && Object.keys(fieldErrors).length === 0 && type.trim()) {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Créer le type
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: ${COLORS.lightBg};
          cursor: not-allowed;
        }
        
        .input-group-text {
          background-color: ${COLORS.lightBg};
          border-color: #dee2e6;
        }
        
        .is-invalid {
          border-color: ${COLORS.danger} !important;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .col-md-4 {
            margin-bottom: 1rem;
          }
        }
      `}</style>
    </Layout>
  );
};

export default CreateIncidentType;